import { Section, Eyebrow } from "@/components/Section";
import { AnimatedNumber } from "@/components/AnimatedNumber";

export interface CyberCrimeStat {
  value: string;
  label: string;
  sub: string;
}

export const DEFAULT_STATS: CyberCrimeStat[] = [
  {
    value: "$166.8M",
    label: "reported lost to payment redirection scams in Australia in 2025",
    sub: "ACCC Targeting Scams report, 2025",
  },
  {
    value: "84,861",
    label: "phishing and email scam reports made by Australians in 2025",
    sub: "ACCC Targeting Scams report, 2025",
  },
  {
    value: "$80k+",
    label: "median reported cost per cyber incident for medium businesses",
    sub: "ACSC Annual Cyber Threat Report, 2023–24",
  },
  {
    value: "~15%",
    label: "of business cyber incidents involve email compromise causing financial loss",
    sub: "ACSC Annual Cyber Threat Report, 2023–24",
  },
];

export function CyberCrimeStats({
  eyebrow = "Why this matters",
  title = "Business email compromise is one of the fastest-growing financial cybercrimes in Australia.",
  body = "Fraudsters impersonate a client, vendor or colleague to redirect a payment — the exact attack pattern hitting trust accounts, payroll runs, refunds, settlements and claim payouts across every industry. Reported losses keep climbing, and once funds leave the account they almost never come back.",
  stats = DEFAULT_STATS,
}: {
  eyebrow?: string;
  title?: string;
  body?: string;
  stats?: CyberCrimeStat[];
}) {
  return (
    <Section
      className="!py-16 border-t border-[hsl(var(--brand-sand))]"
      data-testid="section-cybercrime-stats"
    >
      <div className="grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <Eyebrow>{eyebrow}</Eyebrow>
          <h2 className="mt-3 text-3xl sm:text-4xl font-semibold text-[hsl(var(--brand-charcoal))] leading-tight">
            {title}
          </h2>
          <p className="mt-5 text-[hsl(var(--brand-slate))] leading-relaxed">
            {body}
          </p>
        </div>
        <div className="lg:col-span-7">
          <div className="grid sm:grid-cols-2 gap-px bg-[hsl(var(--brand-sand))] border border-[hsl(var(--brand-sand))] rounded-xl overflow-hidden">
            {stats.map((s) => (
              <div
                key={s.value + s.label}
                className="bg-[hsl(var(--brand-bone))] p-6 sm:p-7"
                data-testid={`stat-${s.value.replace(/[^a-z0-9]/gi, "").toLowerCase()}`}
              >
                <AnimatedNumber
                  value={s.value}
                  className="block text-4xl sm:text-5xl font-semibold text-[hsl(var(--brand-forest))] leading-none"
                />

                <p className="mt-3 text-sm text-[hsl(var(--brand-charcoal))] leading-snug">
                  {s.label}
                </p>
                <div className="mt-4 font-mono text-[10px] tracking-[0.18em] uppercase text-[hsl(var(--brand-slate))]">
                  {s.sub}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-3 text-[11px] font-mono tracking-wide text-[hsl(var(--brand-slate))]">
            Sources: ACCC Targeting Scams report (2025, published March 2026) and ACSC Annual Cyber Threat Report (2023–24). Figures rounded; see source reports for full methodology.
          </div>
        </div>
      </div>
    </Section>
  );
}
